import { cardStyles } from "../../core/Card.styles.js";
import { cn } from "../../foundations/cn.js";
import { FOCUS_RING } from "../../foundations/interaction.js";
import { Amount } from "../Amount.js";
import { ItemArt } from "../ItemArt.js";
import { RarityMarker } from "../RarityMarker.js";
import { RARITY_BORDER } from "../rarity.js";
import { BattleSeat } from "./BattleSeat.js";
import type { BattleResultItem, BattleResultLane } from "./battleTypes.js";

export interface BattleRoundResultsProps {
  /** One per seat, in seat order. */
  lanes: readonly BattleResultLane[];
  roundCount: number;
  /** A round still to show, e.g. (index) => `Round ${index + 1}`. */
  roundLabel?: (index: number) => string;
  /** Names the region. */
  label?: string;
  /** Names a seat's row of pulls, which scrolls on a phone: (name) => `${name}: pulls`. */
  pullsLabel?: (name: string) => string;
  winnerLabel?: string;
  className?: string;
}

/**
 * What each seat has pulled so far: a column per seat, its total under its name, then a card per
 * round that fills in as the round is shown (a numbered placeholder before), edged in the pull's
 * tier. Seats sit side by side from `md`; on a phone each seat is a row whose cards scroll.
 */
export function BattleRoundResults({
  lanes,
  roundCount,
  roundLabel = (index) => `Round ${index + 1}`,
  pullsLabel = (name) => `${name}: pulls`,
  label = "Results",
  winnerLabel,
  className,
}: BattleRoundResultsProps) {
  const rounds = Array.from({ length: roundCount }, (_, index) => index);
  return (
    <section
      aria-label={label}
      className={cn("grid grid-cols-1 gap-3 md:grid-cols-(--lanes) md:gap-4", className)}
      style={{ ["--lanes" as string]: `repeat(${Math.max(1, lanes.length)}, minmax(0, 1fr))` }}
    >
      {lanes.map((lane, index) => (
        <div
          // biome-ignore lint/suspicious/noArrayIndexKey: a seat's place is its identity
          key={index}
          className={cn(
            cardStyles({ radius: "2xl" }),
            "flex min-w-0 flex-col gap-3 p-3",
            lane.seat.isWinner && "border-accent-default",
          )}
        >
          <div className="flex min-w-0 items-center justify-between gap-2">
            <BattleSeat seat={lane.seat} winnerLabel={winnerLabel} />
            <Amount cents={lane.totalCents} size="meta" className="text-text-secondary" />
          </div>
          <ol
            aria-label={pullsLabel(lane.seat.label)}
            // biome-ignore lint/a11y/noNoninteractiveTabindex: it scrolls on a phone, so it must be reachable (WCAG 2.1.1).
            tabIndex={0}
            className={cn(
              "flex gap-2 overflow-x-auto rounded-md md:grid md:grid-cols-[repeat(auto-fill,minmax(4.5rem,1fr))] md:overflow-visible",
              FOCUS_RING,
            )}
          >
            {rounds.map((round) => {
              const item = lane.items[round] ?? null;
              return (
                <li key={round} className="w-18 shrink-0 md:w-auto">
                  {item ? <ResultCard item={item} /> : <Placeholder label={roundLabel(round)} />}
                </li>
              );
            })}
          </ol>
        </div>
      ))}
    </section>
  );
}

function ResultCard({ item }: { item: BattleResultItem }) {
  return (
    <figure className="flex flex-col gap-1">
      <span className={cn("block rounded-md border-2 bg-bg-inset p-1", RARITY_BORDER[item.tier])}>
        <ItemArt src={item.imageUrl} alt="" />
      </span>
      <figcaption className="flex min-w-0 flex-col">
        <span className="truncate font-body text-body-b3 text-text-secondary">{item.name}</span>
        <span className="flex items-center gap-1">
          <RarityMarker tier={item.tier} showLabel={false} size="sm" />
          <Amount cents={item.value} size="meta" compact className="text-text-primary" />
        </span>
      </figcaption>
    </figure>
  );
}

function Placeholder({ label }: { label: string }) {
  return (
    <span className="flex aspect-[63/88] items-center justify-center rounded-md border border-border-subtle border-dashed bg-bg-inset p-1 text-center font-mono text-mono-meta text-text-tertiary">
      {label}
    </span>
  );
}
